import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { api } from '@/lib/api'
import { subscribe } from '@/lib/centrifugo'

export const Route = createFileRoute('/grpc/')({
  component: GrpcPage,
})

function GrpcPage() {
  const [endpoints, setEndpoints] = useState<any[]>([])
  const [url, setUrl] = useState('')
  const [token, setGrpcToken] = useState('')
  const [maxConn, setMaxConn] = useState(2)
  const [maxAge, setMaxAge] = useState(300)
  const [slotMap, setSlotMap] = useState<Record<string, number>>({})

  const load = () => api.getGrpc().then(setEndpoints).catch(() => {})

  useEffect(() => {
    load()
    const iv = setInterval(load, 3000)
    let cleanup: (() => void) | undefined
    subscribe('live:grpc', (data: any) => {
      if (data?.endpoint_id != null && data?.slot != null) {
        setSlotMap((prev) => ({
          ...prev,
          [`${data.endpoint_id}:${data.connection_id}`]: data.slot,
        }))
      }
    }).then((unsub) => { cleanup = unsub })
    return () => {
      clearInterval(iv)
      cleanup?.()
    }
  }, [])

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!url.trim()) return
    await api.addGrpc({ url: url.trim(), token: token.trim() || undefined, max_connections: maxConn, max_age_secs: maxAge })
    setUrl('')
    setGrpcToken('')
    load()
  }

  const handleDelete = async (id: number) => {
    await api.deleteGrpc(id)
    load()
  }

  const handleKill = async (endpointId: number, connId: number) => {
    await api.killConnection(endpointId, connId)
    load()
  }

  const handleSpawn = async (endpointId: number) => {
    await api.spawnConnection(endpointId)
    load()
  }

  const handleToggle = async (id: number, enabled: boolean) => {
    await api.updateGrpc(id, { enabled: !enabled })
    load()
  }

  return (
    <div>
      <h1 style={{ fontSize: 20, marginBottom: 20 }}>GRPC Endpoints</h1>

      <form onSubmit={handleAdd} className="card" style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <input placeholder="gRPC URL" value={url} onChange={(e) => setUrl(e.target.value)} style={{ flex: 1, minWidth: 200 }} />
        <input placeholder="Token (optional)" value={token} onChange={(e) => setGrpcToken(e.target.value)} style={{ width: 160 }} />
        <input type="number" placeholder="Max conn" value={maxConn} onChange={(e) => setMaxConn(+e.target.value)} style={{ width: 80 }} />
        <input type="number" placeholder="Max age (s)" value={maxAge} onChange={(e) => setMaxAge(+e.target.value)} style={{ width: 100 }} />
        <button type="submit" className="btn btn-primary">Add Endpoint</button>
      </form>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {endpoints.map((item: any) => {
          const ep = item.endpoint
          const st = item.status
          return (
            <div key={ep.id} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{ep.url}</div>
                  <div style={{ color: 'var(--text-dim)', fontSize: 12 }}>
                    Max connections: {ep.max_connections} &middot; Max age: {ep.max_age_secs}s &middot;{' '}
                    <span style={{ color: 'var(--green)' }}>{st.tx_per_second?.toFixed(1)} tx/s</span> &middot;{' '}
                    {st.total_tx?.toLocaleString()} total
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-primary btn-sm" onClick={() => handleSpawn(ep.id)}>+ Conn</button>
                  <button className="btn btn-sm" style={{ background: 'var(--bg-hover)', color: 'var(--text)' }} onClick={() => handleToggle(ep.id, ep.enabled)}>
                    {ep.enabled ? 'Disable' : 'Enable'}
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(ep.id)}>Delete</button>
                </div>
              </div>

              {st.connections && st.connections.length > 0 && (
                <table>
                  <thead>
                    <tr>
                      <th>Connection ID</th>
                      <th>Age</th>
                      <th>Slot</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {st.connections.map((c: any) => {
                      const slot = slotMap[`${ep.id}:${c.id}`] ?? c.slot
                      return (
                        <tr key={c.id}>
                          <td style={{ fontFamily: 'monospace' }}>#{c.id}</td>
                          <td>{c.age_secs}s</td>
                          <td style={{ fontFamily: 'monospace' }}>{slot ? slot.toLocaleString() : '-'}</td>
                          <td>
                            <button className="btn btn-danger btn-sm" onClick={() => handleKill(ep.id, c.id)}>Kill</button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )
        })}
        {endpoints.length === 0 && <div style={{ color: 'var(--text-dim)' }}>No endpoints configured</div>}
      </div>
    </div>
  )
}
